import { useCallback, useMemo, useRef, useState } from 'react';
import { DEFAULT_MESSAGE_SORT, type MessageSortOrder } from '@/api';

/**
 * The chosen message order, remembered per ACCOUNT.
 *
 * Per account, not per folder: reading order is a habit rather than a property
 * of a mailbox, and a preference that resets every time someone clicks Sent is
 * not a preference. There is a second reason too — the choice is only valid
 * for orders the account's SERVER can perform, and that is an account-level
 * fact.
 *
 * It lives in localStorage rather than on the server because it is a view
 * preference for this browser, it must survive a reload without a round trip,
 * and there is no settings endpoint that owns it. Storage can throw (private
 * windows, blocked site data), so every access is guarded and the failure mode
 * is simply that the choice does not persist.
 *
 * ## Why the available list is remembered too
 *
 * IMAP SORT is an optional extension and the backend REFUSES an order the
 * server cannot do (501). A remembered order therefore has to be checked
 * before it is REQUESTED, and on a cold load nothing has come back from the
 * server yet. So `rememberSortsAvailable` stores what the server last said,
 * and that is what the next cold load checks against. It writes and never sets
 * state: within a session the caller already has the live list in hand.
 */

const STORE_PREFIX = 'ols-mail.sort.';

interface StoredSort {
  sort: MessageSortOrder;
  available?: MessageSortOrder[];
}

function storeKey(accountId: string | undefined): string | undefined {
  return accountId === undefined || accountId === '' ? undefined : STORE_PREFIX + accountId;
}

function read(accountId: string | undefined): StoredSort {
  const key = storeKey(accountId);
  if (key === undefined) return { sort: DEFAULT_MESSAGE_SORT };
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return { sort: DEFAULT_MESSAGE_SORT };
    const parsed = JSON.parse(raw) as StoredSort;
    // The backend is the authority on which strings are valid orders, so only
    // the shape is checked here; an order this server will not serve is
    // clamped below rather than rejected at the door.
    if (typeof parsed?.sort !== 'string') return { sort: DEFAULT_MESSAGE_SORT };
    return parsed;
  } catch {
    return { sort: DEFAULT_MESSAGE_SORT };
  }
}

function write(accountId: string | undefined, value: StoredSort): void {
  const key = storeKey(accountId);
  if (key === undefined) return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage blocked — the choice just won't survive a reload */
  }
}

export interface MessageSortState {
  /** The order to request. Never one this account's server is known to refuse. */
  sort: MessageSortOrder;
  setSort: (next: MessageSortOrder) => void;
  /**
   * `Paginated.sortsAvailable` from the page that just arrived, kept for the
   * next cold load. Safe to call on every render with the same value.
   */
  rememberSortsAvailable: (available: MessageSortOrder[] | undefined) => void;
}

export function useMessageSort(accountId: string | undefined): MessageSortState {
  const [stored, setStored] = useState<StoredSort>(() => read(accountId));
  // An account switch is a different preference, not the same one carried
  // over. Adjusted during render rather than in an effect: an effect would
  // paint one frame of the previous account's order first.
  const [forAccount, setForAccount] = useState(accountId);
  if (forAccount !== accountId) {
    setForAccount(accountId);
    setStored(read(accountId));
  }

  const written = useRef<string | undefined>(undefined);
  const rememberSortsAvailable = useCallback(
    (available: MessageSortOrder[] | undefined) => {
      if (available === undefined) return;
      const mark = `${accountId ?? ''}|${available.join(',')}`;
      if (written.current === mark) return;
      written.current = mark;
      write(accountId, { sort: read(accountId).sort, available });
    },
    [accountId],
  );

  const setSort = useCallback(
    (next: MessageSortOrder) => {
      setStored((current) => {
        const value = { ...current, sort: next };
        write(accountId, value);
        return value;
      });
    },
    [accountId],
  );

  const sort = useMemo(
    // With nothing yet known about the server the remembered choice stands:
    // the alternative is resetting everyone to the default on every load.
    () =>
      stored.available === undefined || stored.available.includes(stored.sort)
        ? stored.sort
        : DEFAULT_MESSAGE_SORT,
    [stored],
  );

  return { sort, setSort, rememberSortsAvailable };
}
